/* eslint-disable @typescript-eslint/no-magic-numbers */
import { computed, inject, Injectable, NgZone, signal } from '@angular/core';
import { BehaviorSubject, catchError, debounceTime, EMPTY, from, iif, Observable, of, Subject, switchMap, tap, withLatestFrom } from 'rxjs';
import { EnhancePatientNotePromptType, PatientNotesDataService } from '@ffd/api-client';
import { ConfigService, HistoryStackService, HistoryElementWithCursor, ToastService, TokenService } from '@libs/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { ActiveTranscriptionService } from '@libs/speech-recognition';
import { Dialog } from '@angular/cdk/dialog';
import { fromReadableStream } from 'libs/notes/src/lib/components/notes-screen/notes-utils';
import { DataState, routes } from '@libs/utils';
import { HttpErrorResponse } from '@angular/common/http';
import { HealthHistoryDialogService } from '@libs/home';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom, tapResponse } from '@ngrx/operators';

const MODIFY_NOTE_DEBOUNCE_TIME_MS = 1000;

export interface PatientNotesHistoryElement extends HistoryElementWithCursor {
  patientNotes: string;
}

interface HealthHistoryNotesState {
  siteId: string;
  patientId: string;
  patientNotes: string;
  isGenerating: boolean;
  lastFocusedCursorOffset: number;
  allowErrorModal: boolean;
  patientNotesSavingStatus?: DataState;
}

@Injectable()
export class HealthHistoryNotesService extends ComponentStore<HealthHistoryNotesState> {
  public configService = inject(ConfigService);
  public tokenService = inject(TokenService);
  public dialog = inject(Dialog);
  public ngZone = inject(NgZone);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly activeTranscriptionService = inject(ActiveTranscriptionService);
  private readonly patientNotesDataService = inject(PatientNotesDataService);
  private readonly historyStackService = inject<HistoryStackService<PatientNotesHistoryElement>>(HistoryStackService);
  private readonly healthHistoryDialogService = inject(HealthHistoryDialogService);

  constructor() {
    super({
      siteId: '',
      patientId: '',
      patientNotes: '',
      isGenerating: false,
      lastFocusedCursorOffset: 0,
      allowErrorModal: true,
    });
  }

  private readonly transcription = this.activeTranscriptionService.transcriptionControl;
  private abortController = new AbortController();
  private abortSignal = this.abortController.signal;
  private readonly noteTypingSubject$ = new Subject<{ notes: string }>();

  private readonly enhanceRange = signal<{ textStart?: number; textEnd?: number; originalNote?: string }>({
    textStart: undefined,
    textEnd: undefined,
    originalNote: undefined,
  });

  // Accumulator for enhanced text chunks during streaming
  private enhancedTextAccumulator: string = '';

  // controlledNote$ is the note that is currently being edited, but it may not contain the changes which has been typed in manually in the text field.
  // because we want to keep the cursor position, we do not re-render the UI when the note is changed manually
  public controlledNote$ = new BehaviorSubject<string>('');
  public redoDisabledSignal = computed(() => this.historyStackService.isFirstPosition() || this.isGeneratingSignal());
  public undoDisabledSignal = computed(() => this.historyStackService.isLastPosition() || this.isGeneratingSignal());
  public isGeneratingSignal = computed(() => this.state().isGenerating);
  public currentHistoryElementSignal = computed(() => this.historyStackService.getCurrentItem());
  public lastFocusedCursorOffsetSignal = this.selectSignal((state) => state.lastFocusedCursorOffset);

  public readonly isPatientNotesSavingInProgress = this.selectSignal((state) => state.patientNotesSavingStatus === DataState.Loading);
  public readonly isPatientNotesSaved = this.selectSignal((state) => state.patientNotesSavingStatus === DataState.Fetched);
  public readonly patientNotes = computed(() => this.healthHistoryDialogService.viewModel().patientNotes);
  public readonly patientNotesLoading = computed(() => this.healthHistoryDialogService.viewModel().patientNotesLoading);

  public init = this.updater((_): HealthHistoryNotesState => {
    const initialPatientNotes = this.state().patientNotes.length > 0 ? this.state().patientNotes : this.patientNotes();

    this.setControlledNote(initialPatientNotes);
    this.updateLastFocusedCursorOffset(initialPatientNotes.length);

    this.historyStackService.init({
      patientNotes: initialPatientNotes,
      cursorOffset: initialPatientNotes.length,
    });

    return {
      siteId: this.healthHistoryDialogService.siteIdSignal(),
      patientId: this.healthHistoryDialogService.patientIdSignal(),
      patientNotes: initialPatientNotes,
      isGenerating: false,
      lastFocusedCursorOffset: initialPatientNotes.length,
      allowErrorModal: true,
    };
  });

  // Voice typing related functions:

  public readonly toggleSpeechRecognitionListening = this.effect(($) =>
    $.pipe(
      tap(() => {
        this.activeTranscriptionService.toggleTranscription(
          {
            onStop: (result) => {
              const selectionStartOffset = this.state().lastFocusedCursorOffset;
              const editModeText = this.controlledNote$.value;
              const newNote =
                selectionStartOffset === 0
                  ? `${result.transcription}${editModeText}`
                  : `${editModeText.slice(0, selectionStartOffset)}${result.transcription}${editModeText.slice(selectionStartOffset)}`;
              const newCursorOffset = selectionStartOffset + result.transcription.length;

              this.addToHistoryStack({ patientNotes: newNote, cursorOffset: newCursorOffset });
              this.setControlledNote(newNote);
              this.saveNotes();
              this.updateLastFocusedCursorOffset(newCursorOffset);
            },
            onError: (error) => {
              console.error('Voice typing error:', error);
            },
          },
          false,
        );
      }),
    ),
  );

  public restartSpeechRecognition(): void {
    this.activeTranscriptionService.restartTranscription();
  }

  public selectMicrophone(deviceId: string): void {
    this.activeTranscriptionService.selectMicrophone(deviceId);
  }

  public openAudioInputErrorModal(): void {
    this.activeTranscriptionService.openAudioInputSelectorModal();
  }

  public updateLastFocusedCursorOffset = this.updater((state, lastFocusedCursorOffset: number): HealthHistoryNotesState => {
    return { ...state, lastFocusedCursorOffset };
  });

  // AI related functions:

  private readonly appendEnhancedNote = (noteChunk: string): void => {
    // Accumulate the enhanced text chunks
    this.enhancedTextAccumulator += noteChunk;

    // Update last focused cursor offset accordingly
    const newCursorOffset = this.state().lastFocusedCursorOffset + noteChunk.length;
    this.updateLastFocusedCursorOffset(newCursorOffset);

    // Update the display but don't update the history stack yet - that happens on completion
    this.controlledNote$.next(this.enhancedTextAccumulator);
    this.setPatientNotes(this.enhancedTextAccumulator);
  };

  private readonly appendEnhancedNoteWithTextRange = (noteChunk: string, textStart: number, textEnd: number): void => {
    const enhanceRangeValue = this.enhanceRange();
    const originalNote = enhanceRangeValue.originalNote || '';

    // Accumulate the enhanced text chunks
    this.enhancedTextAccumulator += noteChunk;

    // Update last focused cursor offset accordingly
    const newCursorOffset = textStart + this.enhancedTextAccumulator.length;
    this.updateLastFocusedCursorOffset(newCursorOffset);

    // Construct and display the note with the accumulated enhanced text
    // but don't update the history stack yet - that happens on completion
    const newNoteText = originalNote.slice(0, textStart) + this.enhancedTextAccumulator + originalNote.slice(textEnd);
    this.controlledNote$.next(newNoteText);
    this.setPatientNotes(newNoteText);
  };

  public abortEnhance = this.effect(($) => {
    return $.pipe(
      withLatestFrom(this.select((state) => state.isGenerating)),
      tap(([_, isGenerating]) => {
        if (isGenerating) {
          this.abortController.abort();
        }
      }),
    );
  });

  // TODO: fix error handling, should be able to do another enhance after error
  public enhanceNote = this.effect<{
    noteInput: string;
    enhanceType: EnhancePatientNotePromptType;
    textStart?: number;
    textEnd?: number;
    fullNote?: string;
  }>(
    (
      $: Observable<{
        noteInput: string;
        enhanceType: EnhancePatientNotePromptType;
        textStart?: number;
        textEnd?: number;
        fullNote?: string;
      }>,
    ) => {
      const encoder = new TextDecoder();
      return $.pipe(
        tap(({ fullNote, textStart, textEnd }) => {
          // Store the original note and range info
          this.enhanceRange.set({
            textStart,
            textEnd,
            originalNote: textStart !== undefined && textEnd !== undefined ? fullNote : undefined,
          });

          // Reset the accumulator for new enhancement
          this.enhancedTextAccumulator = '';

          this.patchState({ isGenerating: true });
        }),
        concatLatestFrom(() => this.tokenService.getAccessToken$({ isUserActivity: true })),
        switchMap(([{ noteInput, enhanceType }, token]) => {
          return from(
            fetch(`${this.configService.config()?.apiBasePath}/v1/patient-notes/enhance-stream`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                note: noteInput,
                enhanceType: enhanceType,
              }),
            }),
          ).pipe(
            catchError((error) => {
              console.error('Error while enhancing note', error);
              this.toastService.error(this.translateService.instant('health_history_notes_section_enhance_error'));
              this.abortController = new AbortController();
              this.abortSignal = this.abortController.signal;
              this.patchState({ isGenerating: false });
              return EMPTY;
            }),
          );
        }),
        switchMap((response) => {
          if (!response.ok) {
            return from(response.json()).pipe(
              switchMap((responseJson) => {
                if (responseJson.statusCode === 400) {
                  this.toastService.warning(this.translateService.instant('notes_empty_note_error_message'));
                  this.addToHistoryStack({
                    patientNotes: '',
                    cursorOffset: 0,
                  });
                }
                if (responseJson.statusCode === 403) {
                  this.toastService.error(this.translateService.instant('notes_ai_token_exceeded'));
                }
                this.patchState({ isGenerating: false });
                return EMPTY;
              }),
            );
          }

          return response.body
            ? fromReadableStream(response.body, { ngZone: this.ngZone, signal: this.abortSignal }).pipe(
                tap({
                  next: (chunk) => {
                    const text = encoder.decode(chunk);
                    if (this.enhanceRange().textStart !== undefined && this.enhanceRange().textEnd !== undefined) {
                      this.appendEnhancedNoteWithTextRange(text, this.enhanceRange().textStart!, this.enhanceRange().textEnd!);
                    } else {
                      this.appendEnhancedNote(text);
                    }
                  },
                  error: () => {
                    console.error('Error while enhancing note');
                    this.toastService.error(this.translateService.instant('health_history_notes_section_enhance_error'));
                    this.abortController = new AbortController();
                    this.abortSignal = this.abortController.signal;
                    this.patchState({ isGenerating: false });
                  },
                  complete: () => {
                    this.abortController = new AbortController();
                    this.abortSignal = this.abortController.signal;
                    this.patchState({ isGenerating: false });

                    // Now that enhancement is complete, add the final result to history stack
                    const finalNote = this.controlledNote$.value;
                    this.addToHistoryStack({
                      patientNotes: finalNote,
                      cursorOffset: this.state().lastFocusedCursorOffset,
                    });

                    this.saveNotes();
                  },
                }),
              )
            : EMPTY;
        }),
      );
    },
  );

  // History related functions:

  private readonly addToHistoryStack = (note: PatientNotesHistoryElement): void => {
    this.historyStackService.push(note);
  };

  public setControlledNote(note: string): void {
    this.controlledNote$.next(note);
  }

  public handleNoteTyping = this.effect((_$) => {
    return this.noteTypingSubject$.pipe(
      //we have autosave, so here we apply some debounce time to avoid saving the note too often
      debounceTime(MODIFY_NOTE_DEBOUNCE_TIME_MS),
      tap(({ notes }) => {
        // here we do not modify the note purposefully, because we want to keep the cursor position. (otherwise the component would be re-rendered)
        this.addToHistoryStack({
          patientNotes: notes,
          cursorOffset: this.state().lastFocusedCursorOffset,
        });
      }),
      tap(({ notes }) => {
        this.patchState({ patientNotes: notes });
        this.saveNotes();
      }),
    );
  });

  public noteTyping(data: { notes: string }): void {
    this.noteTypingSubject$.next(data);
  }

  public undo = (): void => {
    const currentItem = this.historyStackService.undo();
    if (currentItem) {
      this.setControlledNote(currentItem.patientNotes);
      this.updateLastFocusedCursorOffset(currentItem.cursorOffset);
      this.saveNotes();
    }
  };

  public redo = (): void => {
    const currentItem = this.historyStackService.redo();
    if (currentItem) {
      this.setControlledNote(currentItem.patientNotes);
      this.updateLastFocusedCursorOffset(currentItem.cursorOffset);
      this.saveNotes();
    }
  };

  public resetHistory = (): void => {
    this.historyStackService.reset();
  };

  // Save note related functions:

  public readonly setPatientNotesSavingStatus = this.updater((state, status: DataState): HealthHistoryNotesState => {
    return {
      ...state,
      patientNotesSavingStatus: status,
    };
  });

  public readonly setPatientNotes = this.updater((state, patientNotes: string): HealthHistoryNotesState => {
    return {
      ...state,
      patientNotes,
    };
  });

  public saveNotes = this.effect((note$) =>
    note$.pipe(
      tap(() => this.setPatientNotesSavingStatus(DataState.Loading)),
      debounceTime(MODIFY_NOTE_DEBOUNCE_TIME_MS),
      concatLatestFrom(() => [this.select((state) => state.siteId), this.select((state) => state.patientId)]),
      switchMap(([_, siteId, patientId]) => {
        const note = this.historyStackService.getCurrentItem()?.patientNotes ?? this.state().patientNotes;
        return iif(
          () => siteId === routes.showcaseSiteId,
          of(EMPTY),
          this.patientNotesDataService.setPatientNote(siteId, patientId, { text: note }),
        ).pipe(
          tapResponse(
            () => {
              this.setPatientNotesSavingStatus(DataState.Fetched);
              if (siteId === routes.showcaseSiteId) {
                this.healthHistoryDialogService.setPatientNotes([
                  { id: crypto.getRandomValues(new Uint32Array(1))[0], text: note, type: 3 },
                ]);
              }
            },
            (error: HttpErrorResponse) => {
              console.error(error);
              this.setPatientNotesSavingStatus(DataState.Initial);
            },
            () => {
              if (siteId === routes.showcaseSiteId) return;

              this.healthHistoryDialogService.getPatientNotes();
            },
          ),
        );
      }),
    ),
  );
}
